import {Component, Input} from '@angular/core';


import {BaseChartComponent, ChartSeries} from '@rsp/shared/component/graph/base-chart.component';
import {CustomersEnergyUsagesDTO} from '@rsp/models/dto/customers-energy-usage.dto';
import {DateSelectionFormGroup} from '@rsp/shared/component/date-selection/date-selection.form';
import {IntervalEnum} from '@rsp/models/enum/interval.enum';
import * as Highcharts from 'highcharts';
import {TooltipFormatterContextObject} from 'highcharts';
import {UserTrackingService} from '@rsp/shared/services/user-tracking.service';
import DateTimeFormatOptions = Intl.DateTimeFormatOptions;


@Component({
  selector: 'rsp-strombilanz-autarkie-grad-chart',
  templateUrl: '../base-chart.component.html',
})
export class StrombilanzAutarkieGradChartComponent extends BaseChartComponent<CustomersEnergyUsagesDTO> {

  @Input() public percentageDisplay: boolean;
  @Input() public formGroup: DateSelectionFormGroup;

  constructor(private userTracker: UserTrackingService) {
    super();
  }

  render() {
    super.render();

    const chartSeries = this.transform(this._data);

    this.clear();

    chartSeries.forEach((series) => {
      this.chart.addSeries(series as any, false, false);
    });

    const startDate: Date = this.formGroup.value.date.start;
    const endDate: Date = this.formGroup.value.date.end;
    const interval: IntervalEnum = this.formGroup.value.interval;

    this.chart.ref.xAxis[0].update({
      categories: this.getXAxisCategories(startDate, endDate, interval)
    }, false);

    const globalMax = this._data.values.reduce((acc, cur) => {
      return Math.max(cur.consumption, acc);
    }, 0);

    this.chart.ref.yAxis[0].update({
      max: this.percentageDisplay ? 100 : globalMax,
    });

    this.chart.ref.redraw(true);
  }

  protected hasData(): boolean {
    return super.hasData() && !!this.formGroup.value.date;
  }

  protected createChartOptions(): Highcharts.Options {
    const self = this;
    return {
      chart: {
        type: 'column',
        marginTop: 45
      },
      boost: {
        allowForce: true,
        debug: {
          showSkipSummary: false,
          timeBufferCopy: false,
          timeKDTree: false,
          timeRendering: false,
          timeSeriesProcessing: false,
          timeSetup: false,
        },
        enabled: true,
        seriesThreshold: 20,
        useGPUTranslations: true,
        usePreallocated: false,
      },
      title: {
        text: ''
      },
      credits: {
        enabled: false
      },

      xAxis: {
        categories: []
      },
      yAxis: {
        min: 0,
        title: {
          text: ''
        },
        labels: {
          format: '{value} ' + (this.percentageDisplay ? '%' : 'kWh')
        }
      },
      tooltip: {
        formatter: function () {
          return self.tooltipFormatter(this);
        },
        shared: true
      },
      plotOptions: {
        column: {
          stacking: 'normal'
        }
      },
      series: []
    };
  }

  private transform(data: CustomersEnergyUsagesDTO): ChartSeries[] {
    const result: ChartSeries[] = [];

    const events = {
      hide: (e) => {
        this.userTracker.trackEvent('Meine Autarkie Diagramm', 'Ausblenden', (<HTMLInputElement>e.target).name);
      },
      show: (e) => {
        this.userTracker.trackEvent('Meine Autarkie Diagramm', 'Einblenden', (<HTMLInputElement>e.target).name);
      }
    };

    const restStrom: ChartSeries = {
      name: 'Sonstiger Ökostrom',
      data: [],
      color: this.color3,
      events: events
    };

    const cStrom: ChartSeries = {
      name: 'Community Strom',
      data: [],
      color: this.color2,
      events: events
    };

    const pvEigenversorgung: ChartSeries = {
      name: 'Eigenerzeugung',
      data: [],
      color: this.color1,
      events: events
    };

    // consumption = residualConsumption + communityConsumption + selfSupply

    data.values.forEach((dto) => {
      restStrom.data.push(dto.residualConsumption);
      cStrom.data.push(dto.communityConsumption);
      pvEigenversorgung.data.push(dto.selfSupply);
    });


    result.push(restStrom);
    result.push(cStrom);
    result.push(pvEigenversorgung);

    return result;
  }

  private getXAxisCategories(startDate: Date, endDate: Date, interval: IntervalEnum): string[] {
    const validDates: Date[] = [];

    let curDate: Date = new Date(startDate);

    while (curDate.getTime() <= endDate.getTime()) {
      validDates.push(curDate);
      curDate = new Date(curDate);

      switch (interval) {
        case IntervalEnum.YEAR:
          curDate.setMonth(curDate.getMonth() + 1);
          break;

        case IntervalEnum.MONTH:
          curDate.setDate(curDate.getDate() + 1);
          break;

        case IntervalEnum.WEEK:
          curDate.setDate(curDate.getDate() + 1);
          break;

        default:
          curDate.setMonth(curDate.getMonth() + 1);
          break;
      }
    }

    return validDates.map((date: Date) => {
      switch (interval) {
        case IntervalEnum.YEAR:
          return date.toLocaleString('de-DE', {month: 'short'});

        case IntervalEnum.MONTH:
          return '' + date.getDate();

        case IntervalEnum.WEEK:
          return date.toLocaleDateString('de-DE', {weekday: 'short'});
      }

      return date.toISOString();
    });
  }

  private tooltipFormatter(obj: Highcharts.TooltipFormatterContextObject): string {

    if (!this._data) {
      return '';
    }

    const interval: IntervalEnum = this.formGroup.value.interval;
    const startDate: Date = this.formGroup.value.date.start;

    const categoryIndex: number = this.chart.ref.xAxis[0].categories.findIndex((cat) => cat === obj.x.toString());
    const maxValue = this._data.values[categoryIndex].consumption;

    let res = '';

    let dateOptions: DateTimeFormatOptions = {weekday: 'long'};
    const date: Date = new Date(startDate);

    switch (interval) {
      case IntervalEnum.YEAR:
        date.setMonth(categoryIndex);
        dateOptions = {year: 'numeric', month: 'long'};
        break;

      case IntervalEnum.MONTH:
        date.setDate(date.getDate() + categoryIndex);
        dateOptions = {weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'};
        break;

      case IntervalEnum.WEEK:
        date.setDate(date.getDate() + categoryIndex);
        dateOptions = {weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'};
        break;
    }

    res += date.toLocaleDateString(undefined, dateOptions) + '<br>';


    res += this.pointFormatter(obj.points[0], maxValue);
    res += this.pointFormatter(obj.points[1], maxValue);
    res += this.pointFormatter(obj.points[2], maxValue);


    return res;

  }

  private pointFormatter(p: TooltipFormatterContextObject, maxValue: number): string {
    // TODO Prüfung auf Maxvalue ggf. hinfällig, falls consumption = 0 (Anforderung?)
    if (!p) {
      return '';
    }
    const absVal = this.percentageDisplay ? p.y / 100 * maxValue : p.y;
    const percVal = maxValue === 0 ? undefined : this.percentageDisplay ? p.y : p.y / maxValue * 100;

    return `<span style="color:${p.color}">\u25CF</span>
                ${p.series.name}:
                <b>${BaseChartComponent.adaptedDecimal.transform(absVal)} kWh</b>`
      + (percVal ? ` (${BaseChartComponent.adaptedDecimal.transform(percVal)} %)<br>` : '<br>');
  }
}
